import { useEffect, useState } from "react";
import { Mail, X, Tag, CheckCircle } from "lucide-react";
import { apiClient } from "../lib/api";
import { trackEvent } from "../lib/analytics";

/**
 * Newsletter signup popup — appears once per visitor (30s on first session,
 * 60s on subsequent) to capture email in exchange for 10% off.
 *
 *  - Captures email + first name, fires GA4 `newsletter_signup` event
 *  - Suppressed permanently after submit or dismiss-with-Don't-show (key)
 *  - Suppressed 30 days after just dismissing
 *  - Suppressed on /admin, /checkout, /success, /cart (already converting)
 *  - Mobile-aware: smaller card, bottom-anchored
 *  - WELCOME10 code displayed inline on success (avoids back-and-forth)
 *  - Stores email locally so we can re-trigger cart-recovery / referral
 *    flows if user comes back without re-entering email
 */
const KEY = "tc_newsletter_v1";
const HIDE_PREFIXES = ["/admin", "/checkout", "/success", "/cart", "/quote"];
const SUPPRESS_MS = 30 * 24 * 60 * 60 * 1000;

function loadStored() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export default function NewsletterPopup() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (HIDE_PREFIXES.some((p) => window.location.pathname.startsWith(p))) return;
    const stored = loadStored();
    if (stored?.submitted) return; // already converted
    if (stored?.dismissed && Date.now() - stored.dismissed < SUPPRESS_MS) return;
    // Show after 30s on first session, 60s otherwise
    const delay = stored ? 60000 : 30000;
    const t = setTimeout(() => {
      setShow(true);
      trackEvent("newsletter_popup_shown", {});
    }, delay);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try {
      const prev = loadStored() || {};
      localStorage.setItem(KEY, JSON.stringify({ ...prev, dismissed: Date.now() }));
    } catch { /* no-op */ }
    setShow(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    try {
      // Fire-and-forget — UX shouldn't wait on the email service
      apiClient.post("/newsletter/subscribe", {
        email: email.toLowerCase().trim(),
        name: name.trim(),
        source: "popup",
      }).catch(() => { /* silent — we already have the email locally */ });
    } catch { /* no-op */ }
    try {
      localStorage.setItem(KEY, JSON.stringify({
        submitted: Date.now(),
        email: email.toLowerCase().trim(),
        name: name.trim(),
      }));
    } catch { /* no-op */ }
    trackEvent("newsletter_signup", { method: "popup" });
    setSubmitted(true);
    // Stay visible 6s so user can copy the code
    setTimeout(() => setShow(false), 6000);
  };

  if (!show) return null;

  return (
    <div
      data-testid="newsletter-popup"
      className="fixed inset-0 z-[55] bg-black/85 flex items-center justify-center p-4 animate-[fadeIn_200ms_ease-out]"
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="surface-card max-w-md w-full p-7 md:p-9 relative"
      >
        <button
          onClick={dismiss}
          aria-label="Close popup"
          data-testid="newsletter-close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-white"
        >
          <X size={18} />
        </button>

        {submitted ? (
          <div className="text-center">
            <CheckCircle size={40} className="text-[var(--tc-red)] mx-auto mb-3" />
            <h2 className="font-display text-3xl uppercase leading-none mb-3">You're in.</h2>
            <p className="font-body text-sm text-zinc-300 mb-5">
              Here's your <strong>10% off</strong> code — apply at checkout:
            </p>
            <div className="bg-black border-2 border-dashed border-[var(--tc-red)] py-4 mb-4">
              <div className="font-display text-3xl tracking-widest text-white" data-testid="newsletter-welcome-code">
                INSIDER10
              </div>
            </div>
            <p className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">
              We just emailed it to {email}. No spam, ever.
            </p>
          </div>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest text-[var(--tc-red)] mb-3 uppercase">
              <Tag size={14} /> Save 10% on Your First Order
            </div>
            <h2 className="font-display text-3xl md:text-4xl uppercase leading-none mb-3">
              Join the Crew.
            </h2>
            <p className="font-body text-sm text-zinc-300 mb-5 leading-relaxed">
              Drop your email — we'll send your one-time 10% off code + occasional drops & local events. Unsubscribe anytime.
            </p>

            <form onSubmit={submit} className="space-y-3">
              <input
                type="text"
                placeholder="First name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="newsletter-name"
                className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-3 font-body text-sm outline-none"
              />
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="newsletter-email"
                className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-3 font-body text-sm outline-none"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                className="btn-tc-primary w-full inline-flex items-center justify-center gap-2 text-xs py-3"
              >
                <Mail size={14} /> Get My 10% Off
              </button>
            </form>
            <button
              onClick={dismiss}
              data-testid="newsletter-skip"
              className="block w-full mt-3 font-mono text-[10px] tracking-widest uppercase text-zinc-500 hover:text-zinc-300 py-2"
            >
              No thanks, full price for me
            </button>
          </>
        )}
      </div>
    </div>
  );
}
